import React, { useState, useEffect, useRef } from "react";
import { X, GripVertical, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "../../ui/button";

const STATIC_COLUMNS = [
  { key: "nome", label: "Nome" },
  { key: "cpf", label: "CPF" },
  { key: "rg", label: "RG" },
  { key: "email", label: "Email" },
  { key: "telefone", label: "Telefone" },
  { key: "banco", label: "Banco" },
  { key: "agencia", label: "Agência" },
  { key: "conta_corrente", label: "Conta Corrente" },
  { key: "digito_conta", label: "Dígito Conta" },
  { key: "pix", label: "PIX" },
  { key: "tipo_pix", label: "Tipo PIX" },
  { key: "tipo_conta", label: "Tipo Conta" },
  { key: "logradouro", label: "Logradouro" },
  { key: "numero", label: "Número" },
  { key: "complemento", label: "Complemento" },
  { key: "bairro", label: "Bairro" },
  { key: "cidade", label: "Cidade" },
  { key: "estado", label: "Estado" },
  { key: "cep", label: "CEP" },
  { key: "estado_civil", label: "Estado Civil" },
  { key: "nome_conjuge", label: "Nome Cônjuge" },
  { key: "cpf_conjuge", label: "CPF Cônjuge" },
  { key: "status", label: "Status" },
  { key: "tipo_aceite", label: "Tipo Aceite" },
  { key: "valor_credito", label: "Valor Crédito" },
  { key: "valor_proposta_final", label: "Valor Proposta Final" },
  { key: "valor_parcelado", label: "Valor Parcelado" },
  { key: "valor_parcela", label: "Valor Parcela" },
  { key: "percentual_desagio", label: "% Deságio" },
  { key: "parcelas", label: "Parcelas" },
  { key: "valor_parcela_1", label: "Valor Parcela 1º Período" },
  { key: "valor_parcela_2", label: "Valor Parcela 2º Período" },
  { key: "valor_parcela_3", label: "Valor Parcela 3º Período" },
  { key: "valor_fgts", label: "Valor FGTS" },
  { key: "data_primeira_parcela", label: "Data 1ª Parcela" },
  { key: "data_ultima_parcela", label: "Data Última Parcela" },
];

const LS_KEY = "export_cols_v1";

export function ExportColumnsDialog({ open, onClose, onExport, camposValores = [], isExporting }) {
  const [columns, setColumns] = useState([]);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (!open) return;

    const extraKeys = (camposValores || []).map(cv => `extra_${cv.key}`);
    const allStaticKeys = STATIC_COLUMNS.map(c => c.key);

    const buildFresh = () => [
      ...STATIC_COLUMNS.map(c => ({ ...c, enabled: true })),
      ...(camposValores || []).map(cv => ({
        key: `extra_${cv.key}`,
        label: cv.label,
        extra: true,
        enabled: true,
      })),
    ];

    const saved = localStorage.getItem(LS_KEY);
    if (!saved) { setColumns(buildFresh()); return; }

    try {
      const parsed = JSON.parse(saved);
      const validKeys = new Set([...allStaticKeys, ...extraKeys]);
      // Keep order from saved, filter out removed keys
      const filtered = parsed.filter(c => validKeys.has(c.key));
      const savedKeySet = new Set(filtered.map(c => c.key));
      // Append new keys not in saved (e.g. new extra columns)
      const newCols = [...allStaticKeys, ...extraKeys]
        .filter(k => !savedKeySet.has(k))
        .map(k => {
          const st = STATIC_COLUMNS.find(c => c.key === k);
          if (st) return { ...st, enabled: true };
          const cv = (camposValores || []).find(cv => `extra_${cv.key}` === k);
          return { key: k, label: cv?.label || k, extra: true, enabled: true };
        });
      setColumns([...filtered, ...newCols]);
    } catch {
      setColumns(buildFresh());
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleColumn = (key) =>
    setColumns(prev => prev.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c));

  const toggleAll = (enable) =>
    setColumns(prev => prev.map(c => ({ ...c, enabled: enable })));

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    dragItem.current = null;
    dragOverItem.current = null;
    if (from === null || to === null || from === to) return;
    setColumns(prev => {
      const copy = [...prev];
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const handleExport = () => {
    const selected = columns.filter(c => c.enabled).map(c => c.key);
    localStorage.setItem(LS_KEY, JSON.stringify(columns));
    onExport(selected);
  };

  if (!open) return null;

  const enabledCount = columns.filter(c => c.enabled).length;
  const allEnabled = enabledCount === columns.length;
  const noneEnabled = enabledCount === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="text-base font-semibold flex items-center gap-2 text-foreground">
            <FileSpreadsheet className="h-4 w-4 text-primary" />
            Exportar XLSX — Colunas
          </h3>
          <button
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Controls bar */}
        <div className="flex items-center gap-3 px-5 py-2 border-b border-border shrink-0 bg-muted/30">
          <button
            onClick={() => toggleAll(true)}
            disabled={allEnabled}
            className="text-xs text-primary disabled:opacity-40 hover:underline"
          >
            Selecionar todos
          </button>
          <span className="text-muted-foreground text-xs">·</span>
          <button
            onClick={() => toggleAll(false)}
            disabled={noneEnabled}
            className="text-xs text-muted-foreground disabled:opacity-40 hover:underline"
          >
            Desmarcar todos
          </button>
          <span className="ml-auto text-xs text-muted-foreground">
            {enabledCount}/{columns.length} selecionadas
          </span>
        </div>

        {/* Column list */}
        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {columns.map((col, idx) => (
            <div
              key={col.key}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/60 select-none group"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab shrink-0 opacity-50 group-hover:opacity-100" />
              <input
                type="checkbox"
                id={`ecol-${col.key}`}
                checked={col.enabled}
                onChange={() => toggleColumn(col.key)}
                className="shrink-0 accent-primary"
              />
              <label
                htmlFor={`ecol-${col.key}`}
                className="text-sm text-foreground cursor-pointer flex-1 leading-snug"
              >
                {col.label}
                {col.extra && (
                  <span className="ml-1.5 text-xs text-muted-foreground">(extra)</span>
                )}
              </label>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border shrink-0">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isExporting}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={isExporting || noneEnabled}
          >
            {isExporting
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Exportando...</>
              : "Exportar"}
          </Button>
        </div>
      </div>
    </div>
  );
}
